import { useState } from "react";
import { Card, CardContent, Skeleton } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { FileText, Video, BookOpen } from "lucide-react";
import type { ManagedContentRecord } from "@tellescope/types-models";
import type { ContentListBlock as ContentListBlockType } from "../../../shared/schema";
import { MobileList } from "../MobileList";
import { useEnduserContent } from "../../hooks/useEnduserContent";
import { useContentPreview } from "../../hooks/useContentPreview";
import { useContentFilter } from "../../hooks/useItemFilter";
import { ContentPreviewDialog } from "../ContentPreviewDialog";

interface ContentListBlockProps {
  block: ContentListBlockType;
}

function getContentIcon(type: string | undefined) {
  switch (type) {
    case "Video":
      return Video;
    case "PDF":
      return FileText;
    case "Article":
    default:
      return BookOpen;
  }
}

interface ContentCardProps {
  content: ManagedContentRecord;
  onSelect: (content: ManagedContentRecord) => void;
  isLoading: boolean;
  loadingId: string | null;
}

function ContentCard({ content, onSelect, isLoading, loadingId }: ContentCardProps) {
  const Icon = getContentIcon(content.type);
  const isThisLoading = isLoading && loadingId === content.id;

  return (
    <Card
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={() => !isLoading && onSelect(content)}
    >
      <CardContent className="px-4">
        <div className="flex gap-3 items-start">
          {/* Content type icon */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon className={`h-5 w-5 text-primary ${isThisLoading ? "animate-pulse" : ""}`} />
            </div>
          </div>

          {/* Content details */}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm truncate" title={content.title}>
              {content.title}
            </h4>

            {content.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{content.description}</p>
            )}

            {content.category && (
              <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded-full text-xs bg-muted text-muted-foreground">
                {content.category}
              </span>
            )}
          </div>

          {/* Content type badge */}
          <div className="flex-shrink-0">
            <span className="text-xs text-muted-foreground capitalize">{content.type}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ContentListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="px-4">
            <div className="flex gap-3">
              <Skeleton className="w-10 h-10 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <Skeleton className="w-12 h-4" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function ContentListBlock({ block }: ContentListBlockProps) {
  const { t } = useTranslation("content");
  const { itemFilter } = block.props;
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const { content: rawContent, isLoading: contentLoading, error } = useEnduserContent();

  // Apply item filter if configured
  const { filteredItems: content, isLoading: filterLoading } = useContentFilter({
    items: rawContent,
    itemFilter,
  });

  const isLoading = contentLoading || filterLoading;

  const {
    loadAttachmentUrl,
    attachmentUrl,
    previewContent,
    isLoading: isLoadingPreview,
    clearPreview,
  } = useContentPreview({
    onError: (err) => {
      console.error("Content preview failed:", err);
      setLoadingId(null);
    },
  });

  const handleSelectContent = async (selectedContent: ManagedContentRecord) => {
    setLoadingId(selectedContent.id);
    try {
      await loadAttachmentUrl(selectedContent);
    } finally {
      setLoadingId(null);
    }
  };

  const handleClosePreview = () => {
    clearPreview();
    setLoadingId(null);
  };

  if (isLoading) {
    return <ContentListSkeleton count={Math.min(block.props.maxItems ?? 10, 5)} />;
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-4 text-center text-destructive">{t("Failed to load content")}</CardContent>
      </Card>
    );
  }

  return (
    <>
      <MobileList
        items={content}
        renderItem={(item) => (
          <ContentCard
            content={item}
            onSelect={handleSelectContent}
            isLoading={isLoadingPreview}
            loadingId={loadingId}
          />
        )}
        title={block.props.title ?? t("Your Content")}
        maxItems={block.props.maxItems ?? 10}
        emptyMessage={block.props.emptyMessage ?? t("No content available")}
      />

      <ContentPreviewDialog
        content={previewContent}
        attachmentUrl={attachmentUrl}
        isLoading={isLoadingPreview}
        onClose={handleClosePreview}
      />
    </>
  );
}
